import React, { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MailOpen, PenTool, GraduationCap, Receipt, LifeBuoy, FileCheck, Check, RefreshCw, ArrowRight } from "lucide-react";
import { NewContext } from "../utils/ContextProvider";
import ModalGenerico from "./ModalGenerico";
import "./ModalAlertasCobertura.css";

const CATEGORIES = [
  {
    key: "requests",
    label: "Solicitudes pendientes",
    Icon: MailOpen,
    color: "#005EAE",
    path: "/solicitudes",
  },
  {
    key: "requestSignatures",
    label: "Solicitudes pendientes de firma",
    Icon: PenTool,
    color: "#f59e0b",
    path: "/solicitudes",
    navState: { fromPendingSignature: true },
  },
  {
    key: "courses",
    label: "Capacitaciones pendientes",
    Icon: GraduationCap,
    color: "#14b8a6",
    path: "/capacitaciones",
    module: "CAPACITACIONES",
  },
  {
    key: "receipts",
    label: "Recibos pendientes de firma",
    Icon: Receipt,
    color: "#10b981",
    pathFn: (employeeUuid) => `/mis-recibos/${employeeUuid}`,
    module: "SUELDOS",
  },
  {
    key: "tickets",
    label: "Tickets abiertos",
    Icon: LifeBuoy,
    color: "#ef4444",
    path: "/tickets",
    module: "HELPDESK",
  },
];

const ModalPendientes = ({ show, onClose }) => {
  const { pendingSummary, refreshPendingSummary, getId, getModules } =
    useContext(NewContext);
  const navigate = useNavigate();
  const [refreshing, setRefreshing] = useState(false);

  const modules = useMemo(
    () => (typeof getModules === "function" ? getModules() : getModules) || [],
    [getModules]
  );

  const visibleCategories = useMemo(() => {
    if (!pendingSummary) return [];
    return CATEGORIES.filter((cat) => {
      if (cat.module && !modules.includes(cat.module)) return false;
      const total = pendingSummary[cat.key]?.total ?? 0;
      return total > 0;
    });
  }, [pendingSummary, modules]);

  // TODO: reactivar cuando se implemente consentimiento legal
  const showLegalConsent = false;
  //  pendingSummary?.legalConsent && !pendingSummary.legalConsent.accepted;

  const totalPending = useMemo(() => {
    let total = visibleCategories.reduce(
      (sum, cat) => sum + (pendingSummary?.[cat.key]?.total ?? 0),
      0
    );
    if (showLegalConsent) total += 1;
    return total;
  }, [visibleCategories, pendingSummary, showLegalConsent]);

  const handleNavigate = (cat) => {
    const path = cat.pathFn ? cat.pathFn(getId) : cat.path;
    onClose();
    navigate(path, { state: cat.navState || { fromPending: true } });
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await refreshPendingSummary();
    setRefreshing(false);
  };

  return (
    <ModalGenerico
      show={show}
      onClose={onClose}
      titulo="Notificaciones"
      size="md"
      className="modal-alertas-cobertura"
      footer={
        <button className="btn-ghost" onClick={onClose}>
          Cerrar
        </button>
      }
    >
      {/* Top bar */}
      <div className="sn-bulkbar">
        <div className="sn-bulk-left">
          <span className="sn-muted">
            {totalPending} pendiente{totalPending !== 1 ? "s" : ""}
          </span>
        </div>
        <div className="sn-bulk-right">
          <button
            className="btn-ghost"
            onClick={handleRefresh}
            disabled={refreshing}
            style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}
          >
            <RefreshCw size={14} className={refreshing ? "lucide-spin" : ""} />
            Refrescar
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="sn-modal-body">
        {visibleCategories.length === 0 && !showLegalConsent ? (
          <div className="sn-state sn-state-ok">
            <div className="sn-state-icon">
              <Check size={14} />
            </div>
            <p>No hay pendientes</p>
          </div>
        ) : (
          <ul className="sn-list" role="list">
            {visibleCategories.map((cat) => {
              const count = pendingSummary?.[cat.key]?.total ?? 0;
              return (
                <li
                  key={cat.key}
                  className="sn-pending-item"
                  onClick={() => handleNavigate(cat)}
                >
                  <div
                    className="sn-pending-icon"
                    style={{ background: `${cat.color}15`, color: cat.color }}
                  >
                    <cat.Icon size={14} />
                  </div>
                  <div className="sn-pending-info">
                    <span className="sn-pending-label">{cat.label}</span>
                    <span className="sn-pending-count">
                      {count} {count === 1 ? "pendiente" : "pendientes"}
                    </span>
                  </div>
                  <ArrowRight
                    size={14}
                    className="sn-pending-arrow"
                  />
                </li>
              );
            })}

            {showLegalConsent && (
              <li
                className="sn-pending-item"
                onClick={() => {
                  onClose();
                  navigate("/consentimiento-legal");
                }}
              >
                <div
                  className="sn-pending-icon"
                  style={{ background: "#10233B15", color: "#10233B" }}
                >
                  <FileCheck size={14} />
                </div>
                <div className="sn-pending-info">
                  <span className="sn-pending-label">
                    Consentimiento legal pendiente
                  </span>
                  <span className="sn-pending-count">Requiere aceptacion</span>
                </div>
                <ArrowRight
                  size={14}
                  className="sn-pending-arrow"
                />
              </li>
            )}
          </ul>
        )}
      </div>
    </ModalGenerico>
  );
};

export default ModalPendientes;
